/**
 * @file    useMetricsManager.ts
 * @scope   VIEW
 * @summary Orchestration hook for the CPR MetricsManager view: rollup metric CRUD, reorder,
 *          rename and validation state.
 * @consumes useCprContext, RollupMetricModel, common list hooks (touched, drag, action states)
 * @produces State, handlers and derived data the MetricsManager component renders
 * @sideEffects Dispatches rollup-metric actions to the CPR context.
 * @notes The list lives in `CprContext` (single source of truth); only UI state is local.
 */
import { useCallback } from 'react';
import { useCprContext } from '../../state/CprContext';
import {
  RollupMetricModel,
  createRollupMetric,
  isDefaultRollupMetricName,
} from 'models/CrossProgramRollup/RollupMetricModel';
import { useTouchedItems } from '../../../../common/hooks/useTouchedItems';
import { useDragReorder } from '../../../../common/hooks/useDragReorder';
import { useItemActionStates } from '../../../../common/hooks/useItemActionStates';
import {
  computeRollupItemState,
  computeRollupGlobalState,
  getRollupErrors,
  RollupValidationError,
} from './metricsManagerModel';

export const useMetricsManager = () => {
  const { state, dispatch } = useCprContext();
  const items = state.metrics;

  // Pre-existing metrics (edit mode / back-navigation) start as touched.
  const { touchedIds, isListTouched, markTouched, touchList } = useTouchedItems(
    items.map((i) => i.id)
  );
  // WHY separate: focusing a default name clears it, which must NOT flag the name as missing.
  // The name error only shows once the user types in the input or leaves it.
  const { touchedIds: nameTouchedIds, markTouched: markNameTouched } = useTouchedItems(
    items.map((i) => i.id)
  );

  /**
   * Surfaces every error (name and programs) of the given rows.
   * WHY: any list alteration (add/remove/move) means the user is done with the existing rows.
   */
  const touchRows = useCallback(
    (ids: string[]) => {
      markTouched(ids);
      markNameTouched(ids);
    },
    [markTouched, markNameTouched]
  );

  /** Appends an empty rollup metric. Existing rows are validated; the new one stays clean. */
  const handleAddRollup = useCallback(() => {
    touchRows(items.map((i) => i.id));
    dispatch({ type: 'ADD_ROLLUP_METRIC', metric: createRollupMetric(items.length + 1) });
  }, [items, dispatch, touchRows]);

  /** Removes a metric and validates the remaining rows and the list itself. */
  const handleRemoveRollup = useCallback(
    (id: string) => {
      touchRows(items.filter((i) => i.id !== id).map((i) => i.id));
      dispatch({ type: 'REMOVE_ROLLUP_METRIC', id });
      touchList();
    },
    [items, dispatch, touchRows, touchList]
  );

  const handleRenameRollup = useCallback(
    (id: string, displayName: string) => {
      dispatch({ type: 'RENAME_ROLLUP_METRIC', id, displayName });
      markNameTouched([id]);
    },
    [dispatch, markNameTouched]
  );

  /** Leaving the input commits the name, so an empty one is now reported. */
  const handleTitleBlur = useCallback((id: string) => markNameTouched([id]), [markNameTouched]);

  /** Leaving the whole metric row reports its missing programs. */
  const handleMetricLeave = useCallback((id: string) => markTouched([id]), [markTouched]);

  /** Clears the auto-generated name on focus so the user types a fresh one. */
  const handleTitleFocus = useCallback(
    (item: RollupMetricModel) => {
      if (isDefaultRollupMetricName(item.displayName)) {
        dispatch({ type: 'RENAME_ROLLUP_METRIC', id: item.id, displayName: '' });
      }
    },
    [dispatch]
  );

  const getIndexById = useCallback((id: string) => items.findIndex((i) => i.id === id), [items]);

  /** Reorders a metric and validates every row. */
  const handleMove = useCallback(
    (from: number, to: number) => {
      touchRows(items.map((i) => i.id));
      dispatch({ type: 'MOVE_ROLLUP_METRIC', from, to });
    },
    [items, dispatch, touchRows]
  );

  const { handleDragStart, handleDragOver, handleDrop } = useDragReorder({
    getIndexById,
    onMove: handleMove,
  });

  const { globalState, itemStates } = useItemActionStates(
    items,
    computeRollupItemState,
    computeRollupGlobalState
  );

  /** Errors to display for a row; each rule waits for its own interaction before showing. */
  const getItemErrors = useCallback(
    (item: RollupMetricModel): RollupValidationError[] =>
      getRollupErrors(item).filter((error) =>
        error === 'missingName' ? nameTouchedIds.has(item.id) : touchedIds.has(item.id)
      ),
    [touchedIds, nameTouchedIds]
  );

  const showEmptyError = isListTouched && items.length === 0;

  return {
    items,
    globalState,
    itemStates,
    showEmptyError,
    getItemErrors,
    handleAddRollup,
    handleRemoveRollup,
    handleRenameRollup,
    handleTitleFocus,
    handleTitleBlur,
    handleMetricLeave,
    handleDragStart,
    handleDragOver,
    handleDrop,
  };
};
